import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiAlertCircle, FiExternalLink } from 'react-icons/fi';
import { resolveMarketplaceToken, activateMarketplace } from '../servicesV2/marketplaceService';

import betterflyLogo from '../assets/betterfly-logo.png';

const colors = [
  { name: "Sky", hex: "#0ea5e9" },
  { name: "Gold", hex: "#C0A064" },
  { name: "Emerald", hex: "#10b981" },
  { name: "Indigo", hex: "#6366f1" },
  { name: "Rose", hex: "#f43f5e" },
  { name: "Orange", hex: "#f97316" },
];

export default function MarketplaceSignup() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  // States
  const [step, setStep] = useState('loading'); // loading | form | activating | success | error
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState('');
  const [selectedColor, setSelectedColor] = useState('Sky');

  // Form fields
  const [key, setKey] = useState('');
  const [nom, setNom] = useState('');
  const [representantName, setRepresentantName] = useState('');
  const [representantEmail, setRepresentantEmail] = useState('');
  const [raisonSocial, setRaisonSocial] = useState('');
  const [activite, setActivite] = useState('');

  // Result
  const [tenantUrl, setTenantUrl] = useState('');

  // Step 1: Resolve token on mount
  useEffect(() => {
    if (!token) {
      setError('Aucun token de marketplace. Veuillez accéder à cette page depuis Microsoft Marketplace.');
      setStep('error');
      return;
    }

    resolveMarketplaceToken(token)
      .then((data) => {
        setSubscription(data);
        // Pre-fill email from Microsoft
        if (data.beneficiaryEmail || data.purchaserEmail) {
          setRepresentantEmail(data.beneficiaryEmail || data.purchaserEmail);
        }
        setStep('form');
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.message || 'Erreur lors de la résolution du token';
        setError(msg);
        setStep('error');
      });
  }, [token]);

  // Step 2: Activate
  const handleActivate = async (e) => {
    e.preventDefault();
    if (!key.trim() || !nom.trim()) return;

    setStep('activating');
    setError('');

    try {
      const result = await activateMarketplace({
        subscriptionId: subscription.subscriptionId,
        Key: key.trim(),
        Nom: nom.trim(),
        representantName: representantName.trim(),
        representantEmail: representantEmail.trim(),
        RaisonSocial: raisonSocial.trim(),
        Activite: activite.trim(),
        color: selectedColor,
      });

      setTenantUrl(result.tenantUrl);
      setStep('success');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Erreur lors de l'activation";
      setError(msg);
      setStep('form');
    }
  };

  // Handle key input: only lowercase alphanumeric and hyphens
  const handleKeyChange = (e) => {
    const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setKey(val);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 flex items-center gap-4">
          <img src={betterflyLogo} alt="Betterfly" className="h-12 bg-white rounded-lg p-1" />
          <div>
            <h1 className="text-white text-xl font-bold">Betterfly</h1>
            <p className="text-blue-100 text-sm">Configuration de votre espace</p>
          </div>
        </div>

        <div className="p-8">

          {/* ── Loading ── */}
          {step === 'loading' && (
            <div className="flex flex-col items-center py-12">
              <div className="loading"><div className="box" /></div>
              <p className="text-gray-500 mt-4">Chargement de votre abonnement...</p>
            </div>
          )}

          {/* ── Activating ── */}
          {step === 'activating' && (
            <div className="flex flex-col items-center py-12">
              <div className="loading"><div className="box" /></div>
              <p className="text-gray-500 mt-4">Création de votre espace en cours...</p>
              <p className="text-gray-400 text-sm mt-1">Cela peut prendre quelques secondes</p>
            </div>
          )}

          {/* ── Error ── */}
          {step === 'error' && (
            <div className="flex flex-col items-center py-12">
              <FiAlertCircle size={48} className="text-red-500 mb-4" />
              <p className="text-red-600 font-semibold text-center">{error}</p>
            </div>
          )}

          {/* ── Form ── */}
          {step === 'form' && subscription && (
            <>
              {/* Subscription info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm font-semibold text-blue-800 mb-2">Votre abonnement Microsoft Marketplace</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-500">Plan</span>
                  <span className="font-semibold">{subscription.planId || '-'}</span>
                  <span className="text-gray-500">Email</span>
                  <span className="font-semibold">{subscription.beneficiaryEmail || subscription.purchaserEmail || '-'}</span>
                  {subscription.isFreeTrial && (
                    <>
                      <span className="text-gray-500">Type</span>
                      <span className="font-semibold text-green-600">Essai gratuit</span>
                    </>
                  )}
                </div>
              </div>

              {/* Error banner */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-600 font-semibold flex items-center gap-2">
                    <FiAlertCircle /> {error}
                  </p>
                </div>
              )}

              <form onSubmit={handleActivate}>
                {/* Section: Identifiant */}
                <div className="bg-white border rounded-xl p-4 pt-6 relative mb-5">
                  <span className="bg-white border px-2 py-1 text-xs font-semibold absolute top-[-12px] left-3 rounded-full">
                    Votre espace
                  </span>
                  <div className="mb-4">
                    <label className="text-xs text-gray-500 font-semibold mb-1 block">
                      Identifiant de votre espace *
                    </label>
                    <input
                      className="border rounded-lg px-3 py-2 text-sm w-full focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                      placeholder="mon-entreprise"
                      value={key}
                      onChange={handleKeyChange}
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Votre URL : <strong className="text-blue-600">{key || '___'}.betterfly.solutions</strong>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-semibold mb-1 block">
                      Nom de l'entreprise *
                    </label>
                    <input
                      className="border rounded-lg px-3 py-2 text-sm w-full focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                      placeholder="Mon Entreprise SA"
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Section: Contact */}
                <div className="bg-white border rounded-xl p-4 pt-6 relative mb-5">
                  <span className="bg-white border px-2 py-1 text-xs font-semibold absolute top-[-12px] left-3 rounded-full">
                    Contact
                  </span>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-gray-500 font-semibold mb-1 block">Nom du responsable</label>
                      <input
                        className="border rounded-lg px-3 py-2 text-sm w-full focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                        value={representantName}
                        onChange={(e) => setRepresentantName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-semibold mb-1 block">Email</label>
                      <input
                        type="email"
                        className="border rounded-lg px-3 py-2 text-sm w-full focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                        value={representantEmail}
                        onChange={(e) => setRepresentantEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 font-semibold mb-1 block">Raison sociale</label>
                      <input
                        className="border rounded-lg px-3 py-2 text-sm w-full focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                        value={raisonSocial}
                        onChange={(e) => setRaisonSocial(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-semibold mb-1 block">Activité</label>
                      <input
                        className="border rounded-lg px-3 py-2 text-sm w-full focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                        value={activite}
                        onChange={(e) => setActivite(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Section: Theme */}
                <div className="bg-white border rounded-xl p-4 pt-6 relative mb-6">
                  <span className="bg-white border px-2 py-1 text-xs font-semibold absolute top-[-12px] left-3 rounded-full">
                    Thème
                  </span>
                  <label className="text-xs text-gray-500 font-semibold mb-2 block">Couleur</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {colors.map((c) => (
                      <button
                        key={c.name}
                        type="button"
                        className={`px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all ${
                          selectedColor === c.name
                            ? 'ring-2 ring-offset-2 ring-gray-400 scale-105'
                            : 'opacity-80 hover:opacity-100'
                        }`}
                        style={{ backgroundColor: c.hex }}
                        onClick={() => setSelectedColor(c.name)}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
                >
                  Activer mon espace Betterfly
                </button>
              </form>
            </>
          )}

          {/* ── Success ── */}
          {step === 'success' && (
            <div className="flex flex-col items-center py-8">
              <FiCheckCircle size={64} className="text-green-600 mb-4" />
              <h2 className="text-xl font-bold text-green-700 mb-2">Votre espace est prêt !</h2>
              <p className="text-gray-600 mb-6">Votre espace Betterfly a été créé avec succès.</p>

              <a
                href={tenantUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              >
                <FiExternalLink /> Accéder à {tenantUrl}
              </a>

              <div className="mt-6 bg-gray-50 border rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">Identifiants par défaut</p>
                <p className="text-sm font-semibold mt-1">admin@betterfly.ma / admin</p>
                <p className="text-xs text-gray-400 mt-2">Pensez à changer le mot de passe après la première connexion</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
